import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useDispatch } from 'react-redux';
import '../../App.css';
import logo from '../../assets/logo.svg';
import { Link } from 'react-router-dom';
import { signup } from '../../apis/signup';
import Loader from '../loader/Loader';
import { logout } from '../../store/slices/authSlice';
import {
    TextField,
    InputAdornment,
    IconButton,
    Button,
    Box,
    Typography,
    Alert,
    Checkbox,
    FormControlLabel,
    Stack,
    Tooltip,
} from '@mui/material';
import { Info, Visibility, VisibilityOff } from '@mui/icons-material';
import AuthBackground from './AuthBackground';
import AuthContainer from './AuthContainer';
import { COLOR } from '../../configs/theme';
import { ROUTES } from '../../Router';
import ConsentBanner from '../ConsentBanner';
import { EXCLUDED_EMAIL_DOMAINS } from '../../configs/constant';

// Password validation schema - exported for reuse
export const passwordValidationSchema = Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
    .matches(/[0-9]/, 'Password must contain at least one number')
    .matches(/[!@#$%^&*(),.?":{}|<>]/, 'Password must contain at least one special character')
    .required('Password is required');

// Validation schema
const validationSchema = Yup.object({
    firstName: Yup.string()
        .min(2, 'First name must be at least 2 characters')
        .max(50, 'First name must be less than 50 characters')
        .required('First name is required'),
    lastName: Yup.string()
        .min(2, 'Last name must be at least 2 characters')
        .max(50, 'Last name must be less than 50 characters')
        .required('Last name is required'),
    email: Yup.string()
        .email('Invalid email address')
        .required('Email is required')
        .test('excluded-domains', 'Please use your business email address to register.', (value) => {
            if (!value) return true; // Let required validation handle empty values
            const emailDomain = value.split('@')[1]?.toLowerCase();
            return !EXCLUDED_EMAIL_DOMAINS.includes(emailDomain);
        }),
    password: passwordValidationSchema,
    confirmPassword: Yup.string()
        .oneOf([Yup.ref('password'), null], 'Passwords must match')
        .required('Please confirm your password'),
    agreeToTerms: Yup.boolean().oneOf([true], 'You must agree to the Terms and Conditions'),
    // Professional fields
    organizationName: Yup.string()
        .max(100, 'Organization name must be less than 100 characters')
        .required('Organization Name is required'),
});

// Inner form component that uses reCAPTCHA hook (must be inside provider)
const SignupFormWithRecaptcha = () => {
    const { executeRecaptcha } = useGoogleReCaptcha();
    return <SignupForm isRecaptchaEnabled={true} executeRecaptcha={executeRecaptcha} />;
};

// Main form component
const SignupForm = ({ isRecaptchaEnabled, executeRecaptcha = null }) => {
    const [signupSuccess, setSignupSuccess] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [accountType, setAccountType] = useState('personal');

    const formik = useFormik({
        initialValues: {
            firstName: '',
            lastName: '',
            email: '',
            password: '',
            confirmPassword: '',
            agreeToTerms: false,
            accountType: 'personal',
            // Professional fields
            organizationName: '',
            opted_for_newsletter: true,
        },
        validationSchema,
        onSubmit: async (values) => {
            try {
                setErrorMessage('');
                setSignupSuccess(false);

                // Execute reCAPTCHA v3 (only if enabled)
                let recaptchaToken = '';
                if (isRecaptchaEnabled) {
                    if (!executeRecaptcha) {
                        setErrorMessage('reCAPTCHA is not available. Please refresh the page.');
                        return;
                    }

                    try {
                        recaptchaToken = await executeRecaptcha('signup');
                    } catch (recaptchaError) {
                        console.error('reCAPTCHA error:', recaptchaError);
                        setErrorMessage('reCAPTCHA verification failed. Please try again.');
                        return;
                    }
                }

                const signupData = {
                    username: values.email,
                    password: values.password,
                    first_name: values.firstName,
                    last_name: values.lastName,
                    account_type: accountType,
                    email: values.email,
                    organization_name: values.organizationName,
                    opted_for_newsletter: values.opted_for_newsletter,
                };

                // Only include recaptcha_token if captcha is enabled and token exists
                if (isRecaptchaEnabled && recaptchaToken) {
                    signupData.recaptcha_token = recaptchaToken;
                }

                const data = await signup(signupData);
                setSignupSuccess(true);
            } catch (error) {
                // console.log(error, 'errors');
                setErrorMessage(error || 'Signup failed. Please try again.');
            }
        },
    });


    return (
        <div className="login_container min-vh-100">
            <AuthBackground />
            <div className="content_container">
                <Loader isLoading={formik.isSubmitting} />
                <div className="content_box">
                    <img src={logo} alt="Logo" className="logo" />
                    <Typography variant="body1" textAlign="center" marginTop={6} fontWeight={700}>
                        Create your account
                    </Typography>

                    <AuthContainer>
                        {errorMessage && (
                            <Alert severity="error" sx={{ marginBlock: 2, fontWeight: 'bold' }}>
                                {errorMessage}
                            </Alert>
                        )}
                        {signupSuccess && (
                            <Alert 
                                severity="success" 
                                sx={{ 
                                    marginBlock: 2, 
                                    fontWeight: 'bold',
                                    backgroundColor: '#4caf50',
                                    color: '#ffffff',
                                    '& .MuiAlert-icon': {
                                        color: '#ffffff',
                                    },
                                }}
                            >
                                Signup successful! A verification email has been sent. Please verify your email to activate your account.
                            </Alert>
                        )}


                        <form onSubmit={formik.handleSubmit} style={{
                            display: signupSuccess ? 'none' : 'block',
                            transition: 'all 0.3s ease',
                        }}>
                            {/* Name Fields */}
                            <div className="input_group mb-3">
                                <label className="input_label">First Name</label>
                                <TextField
                                    type="text"
                                    name="firstName"
                                    placeholder="Enter your first name"
                                    value={formik.values.firstName}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                                    helperText={formik.touched.firstName && formik.errors.firstName}
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                />
                            </div>

                            <div className="input_group mb-3">
                                <label className="input_label">Last Name</label>
                                <TextField
                                    type="text"
                                    name="lastName"
                                    placeholder="Enter your last name"
                                    value={formik.values.lastName}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                                    helperText={formik.touched.lastName && formik.errors.lastName}
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                />
                            </div>

                            {/* Email */}
                            <div className="input_group mb-3">
                                <label className="input_label">Business Email</label>
                                <TextField
                                    type="email"
                                    name="email"
                                    placeholder="Enter your email"
                                    value={formik.values.email}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.email && Boolean(formik.errors.email)}
                                    helperText={formik.touched.email && formik.errors.email}
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                />
                            </div>

                            {/* Organization Name */}
                            <div className="input_group mb-3">
                                <label className="input_label">Organization Name</label>
                                <TextField
                                    type="text"
                                    name="organizationName"
                                    placeholder="Enter organization name"
                                    value={formik.values.organizationName}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.organizationName && Boolean(formik.errors.organizationName)}
                                    helperText={formik.touched.organizationName && formik.errors.organizationName}
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                />
                            </div>

                            {/* Password */}
                            <div className="input_group mb-3">
                                <Stack direction={'row'} alignItems={'center'} gap={1} mb={1.5}>
                                    <label className="input_label" style={{ marginBottom: 0 }}>
                                        Password
                                    </label>
                                    <Tooltip
                                        placement="right"
                                        arrow
                                        title={
                                            'Password must be at least 8 characters long and include at least one uppercase letter, one lowercase letter, one number, and one special character.'
                                        }
                                    >
                                        <Info fontSize="small" sx={{ color: COLOR.GREY }} />
                                    </Tooltip>
                                </Stack>
                                <TextField
                                    type={showPassword ? 'text' : 'password'}
                                    name="password"
                                    placeholder="Enter your password"
                                    value={formik.values.password}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.password && Boolean(formik.errors.password)}
                                    helperText={formik.touched.password && formik.errors.password}
                                    fullWidth
                                    size="small"
                                    variant="outlined"
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    edge="end"
                                                >
                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </div>

                            {/* Confirm Password */}
                            <div className="input_group mb-3">
                                <label className="input_label">Confirm Password</label>
                                <TextField
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    name="confirmPassword"
                                    placeholder="Confirm your password"
                                    value={formik.values.confirmPassword}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
                                    helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
                                    fullWidth
                                    size="small"
                                    variant="outlined"
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="toggle confirm password visibility"
                                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                    edge="end"
                                                >
                                                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </div>

                            {/* Newsletter Subscription */}
                            <div className="input_group mb-3">
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            name="opted_for_newsletter"
                                            checked={formik.values.opted_for_newsletter}
                                            onChange={formik.handleChange}
                                            sx={{
                                                color: '#153d7c',
                                                '&.Mui-checked': {
                                                    color: '#153d7c',
                                                },
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography variant="body2">
                                            Subscribe to our newsletter for product updates, insights, and exclusive content.
                                        </Typography>
                                    }
                                    sx={{ margin: 0 }}
                                />
                            </div>

                            {/* Terms and Conditions */}
                            <div className="input_group mb-3">
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            name="agreeToTerms"
                                            checked={formik.values.agreeToTerms}
                                            onChange={formik.handleChange}
                                            sx={{
                                                color: '#153d7c',
                                                '&.Mui-checked': {
                                                    color: '#153d7c',
                                                },
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography variant="body2">
                                            I agree to the{' '}
                                            <Link
                                                to={ROUTES.TERMS_CONDITIONS}
                                                style={{
                                                    color: '#153d7c',
                                                    textDecoration: 'none',
                                                    fontWeight: '900',
                                                    fontSize: '0.85rem',
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.target.style.textDecoration = 'underline';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.target.style.textDecoration = 'none';
                                                }}
                                            >
                                                Terms and Conditions
                                            </Link>
                                        </Typography>
                                    }
                                    sx={{ margin: 0 }}
                                />
                                {formik.touched.agreeToTerms && formik.errors.agreeToTerms && (
                                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                        {formik.errors.agreeToTerms}
                                    </Typography>
                                )}
                            </div>

                            {/* Submit Button */}
                            <Button type="submit" variant="contained" disabled={formik.isSubmitting} fullWidth>
                                {formik.isSubmitting ? 'Signing up...' : 'Sign Up'}
                            </Button>
                        </form>

                        {/* Login Link */}
                        <Box sx={{ textAlign: 'center', marginTop: 2 }}>
                            <Typography variant="body2" color="text.primary">
                                Already have an account? <Link to="/login">Login here</Link>
                            </Typography>
                        </Box>
                    </AuthContainer>
                    <p className="copyright">© 2025-2026 Pratham Software. All rights reserved.</p>
                </div>
            </div>
            <ConsentBanner />
        </div>
    );
};

const Signup = () => {
    const dispatch = useDispatch();

    // Auto-logout if user is already logged in (prevents back button access to dashboard)
    // Only check on component mount, not when user state changes
    useEffect(() => {
        // Check if user exists in localStorage (indicates they navigated here while logged in)
        const storedUser = localStorage.getItem('authUser');
        const storedToken = localStorage.getItem('authToken');
        
        if (storedUser || storedToken) {
            // User is logged in, logout them to ensure fresh signup/login
            dispatch(logout());
            // Clear localStorage
            localStorage.removeItem('authUser');
            localStorage.removeItem('authToken');
            localStorage.removeItem('refreshToken');
        }
    }, [dispatch]); // Only run once on mount

    // Check if reCAPTCHA is enabled via environment variable
    // Defaults to true if REACT_APP_ENABLE_RECAPTCHA is not set (backward compatibility)
    const isRecaptchaEnabled = process.env.REACT_APP_ENABLE_RECAPTCHA !== 'false';
    const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
    
    // If captcha is disabled or site key is not provided, render form without captcha
    if (!isRecaptchaEnabled || !siteKey) {
        return <SignupForm isRecaptchaEnabled={false} executeRecaptcha={null} />;
    }

    // If captcha is enabled and site key exists, wrap with provider
    return (
        <GoogleReCaptchaProvider
            reCaptchaKey={siteKey}
            scriptProps={{
                async: false,
                defer: false,
                appendTo: 'head',
                nonce: undefined,
            }}
        >
            <SignupFormWithRecaptcha />
        </GoogleReCaptchaProvider>
    );
};

export default Signup;
